import React, { useEffect, useMemo, useRef } from 'react';
import { useTheme } from 'styled-components';
import { CloseIcon, HeartIcon, PhoneIcon } from '@/components/UI/Icons/Icons';
import { Button } from '@/components/UI/Button/Button';
import { formatNumber } from '@/utils/helpers';
import * as S from '@/components/Ad/SliderWithGallery/SliderWithGallery.styles';
import Gallery, { GalleryRef } from '@/components/Gallery/Gallery';
import {
  GALLERY_ROOT_STYLES,
  SLIDER_ID,
} from '@/components/Ad/SliderWithGallery/SliderWithGallery.constants';
import { useBodyOverflow } from '@/hooks/useBodyOverflow';
import { PhonesBottomSheet } from '@/components/PhonesBottomSheet/PhonesBottomSheet';
import { useOpenContacts } from '@/hooks/useOpenContacts';
import { useAdDetails } from '@/hooks/useAdDetails';
import { useIsAuthor } from '@/hooks/useIsAuthor';
import { useFloatingButtonsVisibility } from '@/hooks/useFloatingButtonsVisibility';
import { AdStatus } from '@/types';
import { SkeletonLoader } from '@/components/SkeletonLoader/SkeletonLoader';
import { useServerDataContext } from '@/contexts';
import { Carousel } from '@/components/UI/Carousel';
import { useGalleryStore } from '@/store/useGalleryStore';

interface SliderWithGalleryProps {
  isBookmarked?: boolean;
  isGalleryOpen: boolean;
  toggleBookmark: () => void;
  toggleGallery: (state: boolean) => void;
  dataStat?: string;
  overlayText?: string;
  canBookmark?: boolean;
}

export const SliderWithGallery: React.FC<SliderWithGalleryProps> = ({
  isBookmarked,
  isGalleryOpen,
  toggleBookmark,
  toggleGallery,
  dataStat,
  overlayText,
  canBookmark,
}) => {
  const theme = useTheme();
  const { adDetails, loading } = useAdDetails();
  const { initialAdDetails } = useServerDataContext();
  const adData = adDetails ?? initialAdDetails;
  const { id, title, status, kinds, price, photosPreview } = adData ?? {};
  const isAuthor = useIsAuthor();
  const { isCallsAvailable } = useFloatingButtonsVisibility();
  const { isContactsOpen, toggleContacts, getPhoneNumbers, phoneNumbers } =
    useOpenContacts();

  const { currentIndex } = useGalleryStore();
  const galleryRef = useRef<GalleryRef>(null);

  useBodyOverflow(isGalleryOpen);

  useEffect(() => {
    if (isGalleryOpen) {
      galleryRef.current?.open();
    } else {
      galleryRef.current?.close();
    }
  }, [isGalleryOpen]);

  function openGallery() {
    galleryRef.current?.open();
    toggleGallery(true);
  }

  function closeGallery() {
    galleryRef.current?.close();
    toggleGallery(false);
  }

  const images = useMemo(
    () => adData?.photos?.map(photo => photo.url) ?? [],
    [adData?.photos]
  );

  const sliderImages = useMemo(
    () => photosPreview?.map(photo => photo.url),
    [photosPreview]
  );

  const rawPhotos = adData?.photosPreview;
  const photos = Array.isArray(rawPhotos) ? rawPhotos : [];
  const firstSlide = photos[0]?.url ?? '';
  const isPlaceholder = firstSlide.includes('ad_i_placeholder');
  const isNoPhotos = isPlaceholder && photos.length === 1;

  const totalImages = images.length;
  const normalizedIndex =
    totalImages > 0
      ? ((currentIndex % totalImages) + totalImages) % totalImages
      : 0;
  const counter = `${normalizedIndex + 1} / ${totalImages}`;

  if (!adData) {
    return <SkeletonLoader height="330px" />;
  }

  if (isNoPhotos) return null;

  return (
    <React.Fragment>
      {sliderImages?.length ? (
        <Carousel
          loading={loading}
          title={title ?? ''}
          kinds={kinds}
          dataStat={dataStat ?? ''}
          sliderId={SLIDER_ID}
          overlayText={overlayText}
          openGallery={openGallery}
          images={sliderImages || []}
          loopSingleImage={sliderImages.length === 1}
        />
      ) : loading ? (
        <SkeletonLoader height="330px" />
      ) : null}

      {isGalleryOpen && (
        <>
          <S.Head>
            <Button
              appearance="transparent"
              size="l"
              onClick={closeGallery}
              aria-label="Close gallery"
            >
              <CloseIcon fill="white" />
            </Button>

            <S.CountGallery $variant="3" $isBig={!canBookmark}>
              {counter}
            </S.CountGallery>

            {canBookmark && (
              <Button
                appearance="transparent"
                size="l"
                onClick={toggleBookmark}
                aria-label="Toggle bookmark"
              >
                <HeartIcon
                  fill={isBookmarked ? theme.palette.brand.main : 'none'}
                  stroke={
                    isBookmarked
                      ? theme.palette.brand.main
                      : theme.palette.system.white
                  }
                />
              </Button>
            )}
          </S.Head>

          <S.Footer>
            <div>
              {price !== null && (
                <S.Price>{formatNumber(price, 2, true)} AZN</S.Price>
              )}

              <S.Description>{title}</S.Description>
            </div>

            {!isAuthor && status === AdStatus.Published && isCallsAvailable && (
              <S.CallButton onClick={() => getPhoneNumbers({ adId: id })}>
                <PhoneIcon />
              </S.CallButton>
            )}
          </S.Footer>

          <PhonesBottomSheet
            isOpen={isContactsOpen}
            close={toggleContacts}
            phones={phoneNumbers}
          />
        </>
      )}

      {images?.length ? (
        <Gallery
          images={images}
          ref={galleryRef}
          sliderId={SLIDER_ID}
          rootStyles={GALLERY_ROOT_STYLES}
          isSimpleView={false}
          onClose={closeGallery}
        />
      ) : null}
    </React.Fragment>
  );
};
